import { X, Send, Check } from 'lucide-react'
import { useState } from 'react'
import { useSharedGroupStore } from '@/stores/sharedGroupStore'
import { useAuthStore } from '@/stores/authStore'
import type { ChatMessage } from '@/lib/sharedTypes'

interface ShareMessageSheetProps {
  message: ChatMessage | null
  currentGroupId: string
  isOpen: boolean
  onClose: () => void
}

export function ShareMessageSheet({ message, currentGroupId, isOpen, onClose }: ShareMessageSheetProps) {
  const sharedGroups = useSharedGroupStore((s) => s.sharedGroups)
  const sendChat = useSharedGroupStore((s) => s.sendChat)
  const user = useAuthStore((s) => s.user)
  const [sentTo, setSentTo] = useState<Set<string>>(new Set())
  const [sending, setSending] = useState<string | null>(null)

  if (!isOpen || !message) return null

  const otherGroups = sharedGroups.filter((g) => g.id !== currentGroupId)

  const handleShare = async (groupId: string) => {
    if (!user || sentTo.has(groupId)) return
    setSending(groupId)
    try {
      const forwardText = message.text
        ? `[전달됨] ${message.userName}: ${message.text}`
        : `[전달됨] ${message.userName}: (미디어 메시지)`

      await sendChat(groupId, {
        text: forwardText,
        ...(message.imageUrl ? { imageUrl: message.imageUrl, imageWidth: message.imageWidth, imageHeight: message.imageHeight } : {}),
        ...(message.fileUrl ? { fileUrl: message.fileUrl, fileName: message.fileName, fileSize: message.fileSize, fileType: message.fileType } : {}),
        ...(message.voiceUrl ? { voiceUrl: message.voiceUrl, voiceDuration: message.voiceDuration } : {}),
        ...(message.memoSnapshot ? { memoSnapshot: message.memoSnapshot, type: 'memo_card' as const } : {}),
        ...(message.taskRef ? { taskRef: message.taskRef, taskSnapshot: message.taskSnapshot, type: 'task_card' as const } : {}),
      })
      setSentTo((prev) => new Set(prev).add(groupId))
    } catch {
      // 전송 실패 무시
    } finally {
      setSending(null)
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-50" onClick={onClose} />
      <div className="fixed inset-x-0 bottom-0 z-50 bg-surface-primary rounded-t-2xl shadow-2xl pb-safe animate-in slide-in-from-bottom duration-200 max-h-[70vh] flex flex-col">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-surface-tertiary" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-base flex-shrink-0">
          <span className="text-sm font-medium text-heading">공유할 채팅방 선택</span>
          <button onClick={onClose} className="p-1.5 rounded-lg text-caption hover-surface">
            <X className="w-4.5 h-4.5" />
          </button>
        </div>

        {/* 메시지 미리보기 */}
        <div className="px-4 py-2.5 border-b border-base flex-shrink-0">
          <p className="text-xs text-caption truncate">
            {message.userName}: {message.text || '(미디어 메시지)'}
          </p>
        </div>

        {/* 그룹 목록 */}
        <div className="flex-1 overflow-y-auto py-2">
          {otherGroups.length === 0 ? (
            <div className="text-center py-8 text-caption text-sm">
              다른 공유 그룹이 없습니다
            </div>
          ) : (
            otherGroups.map((group) => {
              const isSent = sentTo.has(group.id)
              const isSending = sending === group.id
              return (
                <button
                  key={group.id}
                  onClick={() => handleShare(group.id)}
                  disabled={isSent || isSending}
                  className="w-full flex items-center gap-3 px-5 py-3 text-sm hover-surface transition-colors disabled:opacity-60"
                >
                  {/* 그룹 아이콘 */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                    style={{ backgroundColor: group.color }}
                  >
                    {group.icon || group.name.charAt(0)}
                  </div>

                  {/* 그룹 정보 */}
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-heading truncate">{group.name}</p>
                    <p className="text-meta text-caption">{group.memberCount}명</p>
                  </div>

                  {/* 전송 상태 */}
                  {isSent ? (
                    <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  ) : isSending ? (
                    <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  ) : (
                    <Send className="w-4.5 h-4.5 text-primary-500 flex-shrink-0" />
                  )}
                </button>
              )
            })
          )}
        </div>
      </div>
    </>
  )
}
